import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { getApiErrorMessage } from "@/lib/apiErrors";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ChatCircle, X, Bug, Lightbulb, Question, Heart, PaperPlaneTilt } from "@phosphor-icons/react";

export default function FeedbackWidget() {
  const { t } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const kinds = useMemo(() => [
    { key: "bug", label: t.feedback.kinds.bug, icon: Bug, color: "text-red-600" },
    { key: "idea", label: t.feedback.kinds.idea, icon: Lightbulb, color: "text-amber-600" },
    { key: "question", label: t.feedback.kinds.question, icon: Question, color: "text-blueprint" },
    { key: "love", label: t.feedback.kinds.love, icon: Heart, color: "text-pink-600" },
  ], [t]);

  const isPublic = /^\/(login|sign|pay|legal|accept-invite|$)/.test(location.pathname);
  if (!user || isPublic) return null;

  const send = async () => {
    if (!message.trim()) { toast.error(t.feedback.empty); return; }
    setSending(true);
    try {
      await api.post("/feedback", {
        kind, message: message.trim(),
        url: window.location.href,
        user_agent: navigator.userAgent,
      });
      toast.success(t.feedback.thanks);
      setMessage(""); setOpen(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t.feedback.sendFailed));
    } finally { setSending(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="feedback-open"
        title={t.feedback.openTitle}
        className="fixed bottom-6 right-4 z-40 bg-ink text-white border-2 border-ink w-12 h-12 flex items-center justify-center shadow-brutal hover:bg-safety hover:text-ink hover:border-safety transition-colors">
        <ChatCircle size={20} weight="fill" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-4 z-50 w-[92vw] max-w-sm" data-testid="feedback-panel">
      <div className="brutal-card bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">{t.feedback.panelKicker}</div>
            <h3 className="font-display font-black text-lg tracking-tight">{t.feedback.title}</h3>
          </div>
          <button type="button" onClick={() => setOpen(false)} data-testid="feedback-close"
            className="p-1.5 border-2 border-slate-300 hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {kinds.map((k) => {
            const Ic = k.icon;
            return (
              <button key={k.key} type="button" onClick={() => setKind(k.key)}
                data-testid={`feedback-kind-${k.key}`}
                className={`px-3 py-2 border-2 font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 ${
                  kind === k.key ? "bg-ink text-white border-ink" : "bg-white border-slate-300 hover:border-ink"
                }`}>
                <Ic size={14} weight={kind === k.key ? "fill" : "regular"} className={kind === k.key ? "" : k.color} />
                {k.label}
              </button>
            );
          })}
        </div>

        <textarea value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder={kind === "bug"
            ? t.feedback.phBug
            : kind === "idea"
            ? t.feedback.phIdea
            : t.feedback.phDefault}
          rows={4} data-testid="feedback-message"
          className="w-full bg-white border-2 border-slate-300 px-3 py-2 text-sm focus:border-ink focus:outline-none resize-none mb-3" />

        <button type="button" onClick={send} disabled={sending} data-testid="feedback-send"
          className="w-full bg-ink text-white border-2 border-ink py-2.5 font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-blueprint hover:border-blueprint disabled:opacity-50">
          <PaperPlaneTilt size={14} weight="bold" />
          {sending ? t.feedback.sending : t.feedback.send}
        </button>
        <div className="text-[10px] text-steel text-center mt-2 font-mono">
          {t.feedback.urlNote}
        </div>
      </div>
    </div>
  );
}
